/**
 * The MJPEG fallback, read with fetch rather than an <img src>.
 *
 * An <img> pointed at a multipart stream is the usual way to show MJPEG, and
 * it cannot send a header. The predecessor solved that by putting the access
 * token in the URL, which is how hour-long tokens ended up in nginx's logs. Here
 * the stream is fetched with the playback ticket in Authorization, split into
 * JPEGs in this file, and each frame is shown as a blob: image. Counting frames
 * here is also what lets the LIVE badge tell a moving picture from a frozen one.
 */

const HEADER_END = new Uint8Array([13, 10, 13, 10]);
const CONTENT_LENGTH = /content-length:\s*(\d+)/i;
/** A part bigger than this is not a video frame. Dropped rather than buffered forever. */
const MAX_PART_BYTES = 8 * 1024 * 1024;

function indexOf(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  outer: for (let i = from; i <= haystack.length - needle.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

/**
 * Splits ffmpeg's `mpjpeg` output into JPEG frames.
 *
 * Framed by each part's Content-Length rather than by scanning for the JPEG
 * end marker: the length is exact, and ffmpeg writes the header in lower case
 * ("Content-length"), which is why the match ignores case.
 */
export class MjpegParser {
  private buffer: Uint8Array<ArrayBuffer> = new Uint8Array(0);

  push(chunk: Uint8Array): Uint8Array<ArrayBuffer>[] {
    const merged = new Uint8Array(this.buffer.length + chunk.length);
    merged.set(this.buffer);
    merged.set(chunk, this.buffer.length);
    this.buffer = merged;

    const frames: Uint8Array<ArrayBuffer>[] = [];
    for (;;) {
      const headerEnd = indexOf(this.buffer, HEADER_END);
      if (headerEnd < 0) {
        if (this.buffer.length > MAX_PART_BYTES) this.buffer = new Uint8Array(0);
        break;
      }
      const header = new TextDecoder('latin1').decode(this.buffer.subarray(0, headerEnd));
      const length = Number(CONTENT_LENGTH.exec(header)?.[1] ?? NaN);
      const start = headerEnd + HEADER_END.length;
      if (!Number.isFinite(length) || length > MAX_PART_BYTES) {
        // Not a part we can frame. Skip past this header and look again.
        this.buffer = this.buffer.slice(start);
        continue;
      }
      if (this.buffer.length < start + length) break;
      frames.push(this.buffer.slice(start, start + length));
      this.buffer = this.buffer.slice(start + length);
    }
    return frames;
  }
}

/**
 * Read an MJPEG stream until it ends or `signal` aborts, calling `onFrame` per
 * JPEG. Rejects with an operator-readable message when the gateway refuses.
 */
export async function streamMjpeg(
  url: string,
  ticket: string,
  onFrame: (frame: Blob) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${ticket}`, Accept: 'multipart/x-mixed-replace' },
    signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(
      response.status === 503
        ? 'Every MJPEG slot on the server is in use.'
        : `The gateway refused the MJPEG stream (${response.status}).`,
    );
  }
  const parser = new MjpegParser();
  const reader = response.body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    for (const frame of parser.push(value)) {
      onFrame(new Blob([frame], { type: 'image/jpeg' }));
    }
  }
}
